// pages/BillDetail.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { formatCurrency, formatLocalDate } from '../utils';
import BillPaymentModal from '../components/BillPaymentModal';
import { showToast } from '../components/Toast';

const BillDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [payments, setPayments] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const currentMonth = new Date().toISOString().substring(0, 7);
      const [billResponse, paymentsResponse, budgetItemsResponse] = await Promise.all([
        axios.get(`${API_BASE_PATH}/bills/${id}`),
        axios.get(`${API_BASE_PATH}/bill-payments/${id}`),
        axios.get(`${API_BASE_PATH}/budget-items`, { params: { month: currentMonth } })
      ]);

      const billData = billResponse.data;
      setBill(billData);
      setPayments(paymentsResponse.data || []);

      // Get budget items associated with this bill
      const allBudgetItems = budgetItemsResponse.data || [];
      const billBudgetItems = allBudgetItems.filter(item => item.type === 'budget');
      setBudgetItems(billBudgetItems);

      if (billData.budget_id) {
        const budgetItem = billBudgetItems.find(b => b.id === billData.budget_id);
        setBudget(budgetItem);
      }
    } catch (error) {
      console.error('Error fetching bill details:', error);
      showToast('Failed to load bill details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!confirm('Delete this payment? The next due date will be adjusted.')) return;

    try {
      // Delete the payment from bill_payments table
      await axios.delete(`${API_BASE_PATH}/bill-payments/${id}/${payment.payment_date}`);

      // Find and delete the corresponding budget item
      const budgetItem = budgetItems.find(item =>
        item.type === 'bill_payment' &&
        item.paid_date === payment.payment_date
      );

      if (budgetItem) {
        await axios.delete(`${API_BASE_PATH}/budget-items/${budgetItem.id}`);
      }

      showToast('Payment deleted', 'success');
      fetchData();
    } catch (error) {
      console.error('Error deleting payment:', error);
      showToast('Failed to delete payment', 'error');
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setShowPaymentModal(true);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="empty-state">
        <i className="fas fa-exclamation-circle empty-state-icon"></i>
        <h3 className="empty-state-title">Bill not found</h3>
        <button className="btn btn-primary" onClick={() => navigate('/bills')}>
          Back to Bills
        </button>
      </div>
    );
  }

  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const daysUntilDue = bill.next_due_date 
    ? Math.ceil((new Date(bill.next_due_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate('/bills')}
          style={{ marginBottom: '12px' }}
        >
          <i className="fas fa-arrow-left"></i>
          Back to Bills
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <i className="fas fa-file-invoice" style={{ color: 'var(--primary-color)' }}></i>
              {bill.name}
              {bill.auto_pay && (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: 'var(--primary-light)',
                  color: 'var(--primary-color)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="fas fa-robot"></i>
                  AUTO-PAY
                </span>
              )}
            </h1>
            {budget && (
              <div style={{ marginTop: '8px' }}>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }}>
                  Budget: {budget.name}
                </span>
              </div>
            )}
          </div>

          <button 
            className="btn btn-primary"
            onClick={() => setShowPaymentModal(true)}
          >
            <i className="fas fa-dollar-sign"></i>
            Record Payment
          </button>
        </div>
      </div>

      {/* Bill Info Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div className="metric-card">
          <div className="metric-label">
            <i className="fas fa-money-bill-wave"></i>
            Bill Amount
          </div>
          <div className="metric-value">
            {formatCurrency(bill.amount)}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', textTransform: 'capitalize' }}>
            {bill.frequency}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-label">
            <i className="fas fa-calendar-alt"></i>
            Next Due Date
          </div>
          <div className="metric-value" style={{ fontSize: '18px' }}>
            {bill.next_due_date ? formatLocalDate(bill.next_due_date) : 'Not set'}
          </div>
          {daysUntilDue !== null && (
            <div style={{ 
              fontSize: '12px', 
              marginTop: '4px',
              color: daysUntilDue < 0 ? 'var(--danger-color)' : 
                     daysUntilDue < 7 ? 'var(--warning-color)' : 
                     'var(--text-secondary)'
            }}>
              {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} days overdue` : 
               daysUntilDue === 0 ? 'Due today' : 
               `in ${daysUntilDue} days`}
            </div>
          )}
        </div>

        <div className="metric-card">
          <div className="metric-label">
            <i className="fas fa-receipt"></i>
            Total Payments
          </div>
          <div className="metric-value" style={{ color: 'var(--success-color)' }}>
            {payments.length}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            {formatCurrency(totalPaid)} paid
          </div>
        </div>
      </div>

      {/* Notes */}
      {bill.notes && (
        <div style={{
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--border-radius)',
          marginBottom: '24px',
          border: '1px solid var(--border-color)'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
            NOTES
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
            {bill.notes}
          </div>
        </div>
      )}

      {/* Payment History */}
      <div>
        <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>
          Payment History
        </h2>

        {payments.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-history empty-state-icon"></i>
            <h3 className="empty-state-title">No payments recorded</h3>
            <p className="empty-state-description">
              Record your first payment to start tracking history
            </p>
            <button 
              className="btn btn-primary" 
              onClick={() => setShowPaymentModal(true)}
              style={{ marginTop: '16px' }}
            >
              <i className="fas fa-dollar-sign"></i>
              Record First Payment
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => (
                  <tr key={payment.payment_date}>
                    <td>
                      <strong>{formatLocalDate(payment.payment_date)}</strong>
                    </td>
                    <td>
                      <strong style={{ color: 'var(--success-color)' }}>
                        {formatCurrency(payment.amount)}
                      </strong>
                      {payment.amount !== bill.amount && (
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          Regular: {formatCurrency(bill.amount)}
                        </div>
                      )}
                    </td>
                    <td>
                      {payment.is_reconciled ? (
                        <span
                          className="badge-success"
                          style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <i className="fas fa-check-circle"></i>
                          Reconciled
                        </span>
                      ) : (
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'var(--warning-light)',
                            color: 'var(--warning-color)'
                          }}
                        >
                          <i className="fas fa-clock"></i>
                          Pending
                        </span>
                      )}
                    </td>
                    <td>
                      {payment.notes || '-'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn-table-action"
                          onClick={() => navigate(`/transactions`)}
                          title="View in transactions"
                        >
                          <i className="fas fa-external-link-alt"></i>
                        </button>
                        <button
                          className="btn-table-action danger"
                          onClick={() => handleDeletePayment(payment)}
                          title="Delete payment"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <BillPaymentModal
          bill={bill}
          payment={editingPayment}
          onClose={() => {
            setShowPaymentModal(false);
            setEditingPayment(null);
          }}
          onSave={() => {
            setShowPaymentModal(false);
            setEditingPayment(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default BillDetail;